'use client'

import { useQueryClient } from '@tanstack/react-query'

import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgSmartAccount } from '~/types'

// Removing an import archives it, the same call the account menu makes, and
// re-importing the address revives that row. An imported account holds no
// funds and has no signers, so this needs no signing confirmation and can
// never be blocked by a pending transaction the way a real account can.
export const RemoveImportedAccountDialog = ({
  smartAccount,
  closeDialog,
}: {
  smartAccount: Pick<OrgSmartAccount, 'id' | 'name'>
  closeDialog: () => void
}) => {
  const { serverPutRequest } = useServerRequests()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const removeAccount = async () => {
    const result = await serverPutRequest({
      path: '/api/smart_accounts/edit/archive',
      params: { smartAccountId: smartAccount.id },
      successMessage: 'Imported account removed',
      errorMessage: 'Failed to remove imported account',
    })
    if (!result) return

    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.IMPORTED_ACCOUNTS, activeOrgId],
    })
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.ORG_SMART_ACCOUNTS, activeOrgId],
    })
  }

  return (
    <SplitsDialogContent
      id="removeImportedAccount"
      variant="confirmation"
      title={`Remove "${smartAccount.name}"?`}
      confirmButton={{
        text: 'Remove',
        onClick: () => {
          closeDialog()
          void removeAccount()
        },
      }}
    >
      <p>
        Its history stops feeding accounting. You can import the address again
        later.
      </p>
    </SplitsDialogContent>
  )
}
